"use client";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import Button from "@/components/library/Button";
import { Switch } from "@/components/ui/switch";
import { DaysOfWeek } from "@/enums/settings";
import {
  ALL_DAYS,
  DAY_LABEL_KEYS,
  WEEKDAYS,
  WEEKEND_DAYS,
  type DayHoursMap,
  type Slot,
} from "@/utils/onboarding/businessHoursUtils";
import { AnimatedSlotRow, TimeOption, TimeSlotRow } from "./TimeSlotRow";

const PILL_BASE = "px-3 py-1.5 rounded-full text-xs font-medium transition-colors border";
const PILL_ACTIVE = "border-primary bg-primary/10 text-primary";
const PILL_INACTIVE = "border-200 bg-transparent text-foreground hover:bg-100";

interface CustomSchedulePanelProps {
  readonly showCustomDays: boolean;
  readonly showCustomTimes: boolean;
  readonly selectedDays: DaysOfWeek[];
  readonly dayHours: DayHoursMap;
  readonly timeOptions: TimeOption[];
  readonly hasAllWeekdays: boolean;
  readonly hasAllWeekends: boolean;
  readonly onSelectWeekdays: () => void;
  readonly onSelectWeekends: () => void;
  readonly onToggleDay: (day: DaysOfWeek) => void;
  readonly onUpdateGroupSlot: (
    group: DaysOfWeek[],
    slotIndex: number,
    field: "start" | "end",
    value: string
  ) => void;
  readonly onUpdateDaySlot: (
    day: DaysOfWeek,
    slotIndex: number,
    field: "start" | "end",
    value: string
  ) => void;
  readonly onAddTimeSlot: (day: DaysOfWeek) => void;
  readonly onRemoveTimeSlot: (day: DaysOfWeek, index: number) => void;
  readonly onEnterCopyMode: (day: DaysOfWeek) => void;
  readonly onExitCopyMode: () => void;
  readonly onPasteToDay: (day: DaysOfWeek) => void;
  readonly copySourceDay: DaysOfWeek | null;
  // Grouped mode props (only needed for inbound with sync toggles)
  readonly sameHoursAllWeek?: boolean;
  readonly sameHoursAllWeekend?: boolean;
  readonly onSameHoursAllWeekChange?: (checked: boolean) => void;
  readonly onSameHoursAllWeekendChange?: (checked: boolean) => void;
  readonly onAddGroupSlot?: (group: DaysOfWeek[], referenceDay: DaysOfWeek) => void;
  readonly onRemoveGroupSlot?: (group: DaysOfWeek[], slotIndex: number) => void;
}

export function CustomSchedulePanel({
  showCustomDays,
  showCustomTimes,
  selectedDays,
  dayHours,
  timeOptions,
  hasAllWeekdays,
  hasAllWeekends,
  onSelectWeekdays,
  onSelectWeekends,
  onToggleDay,
  onUpdateGroupSlot,
  onUpdateDaySlot,
  onAddTimeSlot,
  onRemoveTimeSlot,
  onEnterCopyMode,
  onExitCopyMode,
  onPasteToDay,
  copySourceDay,
  sameHoursAllWeek,
  sameHoursAllWeekend,
  onSameHoursAllWeekChange,
  onSameHoursAllWeekendChange,
  onAddGroupSlot,
  onRemoveGroupSlot,
}: CustomSchedulePanelProps) {
  const { t } = useTranslation("onboarding");
  const isInCopyMode = copySourceDay !== null;
  const hasGroupedMode = sameHoursAllWeek !== undefined;

  const weekdaysPillClass = clsx(PILL_BASE, hasAllWeekdays ? PILL_ACTIVE : PILL_INACTIVE);
  const weekendsPillClass = clsx(PILL_BASE, hasAllWeekends ? PILL_ACTIVE : PILL_INACTIVE);

  const selectedWeekdays = WEEKDAYS.filter((d) => selectedDays.includes(d));
  const selectedWeekendDays = WEEKEND_DAYS.filter((d) => selectedDays.includes(d));
  const hasAnyWeekend = WEEKEND_DAYS.some((d) => selectedDays.includes(d));

  const groupedWeekdaySlots: Slot[] =
    selectedWeekdays.length > 0
      ? dayHours[selectedWeekdays[0]]
      : [{ start: "09:00", end: "17:00", _id: "default-weekday" }];
  const groupedWeekendSlots: Slot[] =
    selectedWeekendDays.length > 0
      ? dayHours[selectedWeekendDays[0]]
      : [{ start: "09:00", end: "17:00", _id: "default-weekend" }];

  const weekdayGroupRows = sameHoursAllWeek ? "1fr" : "0fr";
  const weekdayPerDayRows = sameHoursAllWeek ? "0fr" : "1fr";
  const weekendGroupRows = sameHoursAllWeekend ? "1fr" : "0fr";
  const weekendPerDayRows = sameHoursAllWeekend ? "0fr" : "1fr";

  const slotCallbacks = {
    updateDaySlot: onUpdateDaySlot,
    addTimeSlot: onAddTimeSlot,
    enterCopyMode: onEnterCopyMode,
    removeTimeSlot: onRemoveTimeSlot,
    exitCopyMode: onExitCopyMode,
    pasteToDay: onPasteToDay,
  };

  const renderDaySlots = (day: DaysOfWeek) => {
    const slots = dayHours[day] as Slot[];
    const isSource = copySourceDay === day;
    const isTarget = isInCopyMode && !isSource;

    return (
      <div key={day} className="space-y-2">
        <AnimatePresence initial={false}>
          {slots.map((slot, slotIndex) => (
            <AnimatedSlotRow
              key={slot._id}
              slot={slot}
              slotIndex={slotIndex}
              day={day}
              isSource={isSource}
              isTarget={isTarget}
              isInCopyMode={isInCopyMode}
              t={t}
              timeOptions={timeOptions}
              callbacks={slotCallbacks}
            />
          ))}
        </AnimatePresence>
      </div>
    );
  };

  const renderGroupSlots = (
    groupDays: DaysOfWeek[],
    slots: Slot[],
    referenceDay: DaysOfWeek
  ) => (
    <div className="space-y-2">
      <AnimatePresence initial={false}>
        {slots.map((slot, slotIndex) => {
          const isFirst = slotIndex === 0;
          const slotKey = slot._id ?? `group-slot-${slotIndex}`;
          const row = (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <TimeSlotRow
                  start={slot.start}
                  end={slot.end}
                  onStartChange={(v) => onUpdateGroupSlot(groupDays, slotIndex, "start", v)}
                  onEndChange={(v) => onUpdateGroupSlot(groupDays, slotIndex, "end", v)}
                  t={t}
                  timeOptions={timeOptions}
                  options={{
                    onAdd:
                      isFirst && onAddGroupSlot
                        ? () => onAddGroupSlot(groupDays, referenceDay)
                        : undefined,
                    onRemove:
                      !isFirst && onRemoveGroupSlot
                        ? () => onRemoveGroupSlot(groupDays, slotIndex)
                        : undefined,
                    showActionsColumn: true,
                  }}
                />
              </div>
            </div>
          );

          if (!isFirst) {
            return (
              <motion.div
                key={slotKey}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                {row}
              </motion.div>
            );
          }

          return <div key={slotKey}>{row}</div>;
        })}
      </AnimatePresence>
    </div>
  );

  return (
    <div className="space-y-6 pt-2">
      {/* Custom Days */}
      {showCustomDays && (
        <div className="space-y-3">
          <label className="text-sm font-medium text-foreground">
            {t("businessHours.customDays")}
          </label>
          <div className="flex items-center flex-wrap gap-2">
            <Button type="none" onClick={onSelectWeekdays} className={weekdaysPillClass}>
              {t("businessHours.weekdays")}
            </Button>
            <Button type="none" onClick={onSelectWeekends} className={weekendsPillClass}>
              {t("businessHours.weekends")}
            </Button>

            <div className="w-px h-5 bg-200 mx-1" />

            {ALL_DAYS.map((day) => (
              <Button
                key={day}
                type="none"
                onClick={() => onToggleDay(day)}
                className={clsx(
                  PILL_BASE,
                  selectedDays.includes(day) ? PILL_ACTIVE : PILL_INACTIVE
                )}
              >
                {t(DAY_LABEL_KEYS[day])}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Custom Times */}
      {showCustomTimes && selectedDays.length > 0 && (
        <div className="space-y-4">
          <label className="text-sm font-medium text-foreground">
            {t("businessHours.customTimes")}
          </label>

          {/* Weekday section */}
          {selectedWeekdays.length > 0 && (
            <div className="space-y-3">
              {hasGroupedMode ? (
                <>
                  {/* Grouped row */}
                  <div
                    className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                    style={{ gridTemplateRows: weekdayGroupRows }}
                  >
                    <div className="overflow-hidden">
                      <div className="space-y-3">
                        {renderGroupSlots(WEEKDAYS, groupedWeekdaySlots, selectedWeekdays[0])}
                        <div className="flex items-center justify-end gap-2">
                          <Switch
                            checked={sameHoursAllWeek}
                            onCheckedChange={onSameHoursAllWeekChange}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {t("businessHours.sameHoursAllWeek")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-day rows */}
                  <div
                    className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                    style={{ gridTemplateRows: weekdayPerDayRows }}
                  >
                    <div className="overflow-hidden">
                      <div className="space-y-2">
                        {selectedWeekdays.map((day) => renderDaySlots(day))}
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <Switch
                            checked={sameHoursAllWeek}
                            onCheckedChange={onSameHoursAllWeekChange}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {t("businessHours.sameHoursAllWeek")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                renderGroupSlots(WEEKDAYS, groupedWeekdaySlots, selectedWeekdays[0])
              )}
            </div>
          )}

          {/* Weekend section */}
          {hasAnyWeekend && (
            <div className="space-y-3">
              {hasGroupedMode ? (
                <>
                  {/* Grouped row */}
                  <div
                    className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                    style={{ gridTemplateRows: weekendGroupRows }}
                  >
                    <div className="overflow-hidden">
                      <div className="space-y-3">
                        {renderGroupSlots(
                          WEEKEND_DAYS,
                          groupedWeekendSlots,
                          selectedWeekendDays[0]
                        )}
                        <div className="flex items-center justify-end gap-2">
                          <Switch
                            checked={sameHoursAllWeekend}
                            onCheckedChange={onSameHoursAllWeekendChange}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {t("businessHours.sameHoursAllWeekend")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Per-day rows */}
                  <div
                    className="grid transition-[grid-template-rows] duration-200 ease-in-out"
                    style={{ gridTemplateRows: weekendPerDayRows }}
                  >
                    <div className="overflow-hidden">
                      <div className="space-y-2">
                        {selectedWeekendDays.map((day) => renderDaySlots(day))}
                        <div className="flex items-center justify-end gap-2 pt-1">
                          <Switch
                            checked={sameHoursAllWeekend}
                            onCheckedChange={onSameHoursAllWeekendChange}
                          />
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            {t("businessHours.sameHoursAllWeekend")}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                renderGroupSlots(WEEKEND_DAYS, groupedWeekendSlots, selectedWeekendDays[0])
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
